import GoogleAccountPage from './pages/GoogleAccountPage';
import { clearSession, readSessionUser, saveSession, saveSessionUser, type SessionUser } from './utils/session';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl, getAuthHeaders } from './config/api';
import Login from './pages/Login';
import PatientList, { createDefaultPatientListViewState, type PatientListViewState } from './pages/PatientList';
import PatientProfile from './pages/PatientProfile';
import Calendar, { type CalendarNavigationState, readStoredPhoneCalendarView } from './pages/Calendar';
import DoctorsList from './pages/DoctorsList';
import DoctorProfile from './pages/DoctorProfile';
import AllergiesCatalog from './AllergiesCatalog';
import CreatePatient from './pages/CreatePatient';
import HomeDashboard from './pages/HomeDashboard';
import { ReportsPage } from './pages/ReportsPage';
import { DoctorTreatmentsPage } from './pages/DoctorTreatmentsPage';
import UserManagement from './pages/UserManagement';
import TreatmentsManagementPage from './pages/TreatmentsManagementPage';
import TreatmentWorkspacePage from './pages/TreatmentWorkspacePage';
import GoogleSheetImportsPage from './pages/GoogleSheetImportsPage';
import WhatsAppPage from './pages/WhatsAppPage';
import InstallPrompt from './components/pwa/InstallPrompt';
import type { IntakeConversionContext, Patient as CalendarPatient } from './components/calendar/types';
import { themeOptions, useTheme } from './theme/ThemeContext';
import ClinixLogo from './components/brand/ClinixLogo';

import { matchesMedia, useMediaQuery } from './hooks/useMediaQuery';
import { useFocusDialog } from './hooks/useFocusDialog';
import './responsive.css';

type ViewType = 'account' | 'home' | 'patients' | 'calendar' | 'doctors' | 'doctorProfile' | 'allergies' | 'createPatient' | 'reports' | 'doctorTreatments' | 'users' | 'treatments' | 'treatmentWorkspace' | 'sheetImports' | 'whatsapp';

const VIEW_STORAGE_KEY = 'dentec:currentView';
const PATIENT_ID_STORAGE_KEY = 'dentec:selectedPatientId';
const DOCTOR_ID_STORAGE_KEY = 'dentec:selectedDoctorId';
const DOCTOR_NAME_STORAGE_KEY = 'dentec:selectedDoctorName';
const MONTH_STORAGE_KEY = 'dentec:selectedMonth';
const TREATMENT_ID_STORAGE_KEY = 'dentec:selectedTreatmentId';
const TREATMENT_PATIENT_ID_STORAGE_KEY = 'dentec:selectedTreatmentPatientId';
const TREATMENT_APPOINTMENT_ID_STORAGE_KEY = 'dentec:selectedTreatmentAppointmentId';

const allowedViews: ViewType[] = [
  'account',
  'home',
  'patients',
  'calendar',
  'doctors',
  'doctorProfile',
  'allergies',
  'createPatient',
  'reports',
  'doctorTreatments',
  'users',
  'treatments',
  'treatmentWorkspace',
  'sheetImports',
  'whatsapp'
];

const isValidView = (value: string | null): value is ViewType => {
  return value !== null && allowedViews.includes(value as ViewType);
};

const readStoredNumber = (key: string): number | null => {
  const raw = localStorage.getItem(key);
  if (raw === null) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

const baseLayoutStyle: React.CSSProperties = { display: 'grid', minHeight: '100vh', transition: 'grid-template-columns 0.2s ease' };
const baseSideNavStyle: React.CSSProperties = { background: '#0f172a', color: '#e2e8f0', display: 'flex', flexDirection: 'column', gap: 8, transition: 'width 0.2s ease, padding 0.2s ease' };
const contentStyle: React.CSSProperties = { minWidth: 0 };

export default function App() {
  const { t, i18n } = useTranslation();
  const { currentTheme, setCurrentTheme, tokens, activeTheme } = useTheme();
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('token'));
  const [sessionUser, setSessionUser] = useState<SessionUser | null>(() => readSessionUser());
  const [userRole, setUserRole] = useState<string | null>(() => localStorage.getItem('userRole'));
  const [selectedPatientId, setSelectedPatientId] = useState<number | null>(() => readStoredNumber(PATIENT_ID_STORAGE_KEY));
  const [selectedDoctorId, setSelectedDoctorId] = useState<number | null>(() => readStoredNumber(DOCTOR_ID_STORAGE_KEY));
  const [selectedDoctorName, setSelectedDoctorName] = useState<string>(() => localStorage.getItem(DOCTOR_NAME_STORAGE_KEY) || '');
  const [selectedMonth, setSelectedMonth] = useState<string>(() => localStorage.getItem(MONTH_STORAGE_KEY) || '');
  const [selectedTreatmentId, setSelectedTreatmentId] = useState<number | null>(() => readStoredNumber(TREATMENT_ID_STORAGE_KEY));
  const [selectedTreatmentPatientId, setSelectedTreatmentPatientId] = useState<number | null>(() => readStoredNumber(TREATMENT_PATIENT_ID_STORAGE_KEY));
  const [selectedTreatmentAppointmentId, setSelectedTreatmentAppointmentId] = useState<number | null>(() => readStoredNumber(TREATMENT_APPOINTMENT_ID_STORAGE_KEY));

  useEffect(() => {
    document.documentElement.lang = String(i18n.language || 'es').toLowerCase().startsWith('es') ? 'es-BO' : 'en-GB';
  }, [i18n.language]);

  const [currentView, setCurrentView] = useState<ViewType>(() => {
    const storedView = localStorage.getItem(VIEW_STORAGE_KEY);
    return isValidView(storedView) ? storedView : 'home';
  });
  const [intakeContext, setIntakeContext] = useState<IntakeConversionContext | null>(null);
  const [pendingCalendarConversion, setPendingCalendarConversion] = useState<{ intake: IntakeConversionContext; patient: CalendarPatient } | null>(null);
  const [desktopCollapsed, setSidebarCollapsed] = useState(false);
  const isPhone = useMediaQuery('(max-width: 767px)');
  const isTablet = useMediaQuery('(min-width: 768px) and (max-width: 1023px)');
  const sidebarCollapsed = !isPhone && (isTablet || desktopCollapsed);
  const [menuOpen, setMenuOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  useFocusDialog(isPhone && menuOpen && !!token, navRef, closeMenu);
  useEffect(() => { if (!isPhone) setMenuOpen(false); }, [isPhone]);
  useEffect(() => {
    const main = mainRef.current;
    if (isPhone && menuOpen) main?.setAttribute('inert', '');
    else main?.removeAttribute('inert');
    return () => main?.removeAttribute('inert');
  }, [isPhone, menuOpen]);
  const [themePickerOpen, setThemePickerOpen] = useState(false);
  const [patientListViewState, setPatientListViewState] = useState<PatientListViewState>(() => createDefaultPatientListViewState());
  const [calendarNavigationState, setCalendarNavigationState] = useState<CalendarNavigationState>(() => ({
    currentDate: new Date(),
    view: matchesMedia('(max-width: 767px)') ? readStoredPhoneCalendarView() : 'week'
  }));

  const effectiveRole = sessionUser?.role || userRole;
  const isAdmin = (effectiveRole || '').toString().toLowerCase() === 'admin';
  const isDoctor = (effectiveRole || '').toString().toLowerCase() === 'doctor';
  const canViewWhatsApp = isAdmin || sessionUser?.canViewWhatsApp === true;

  // Refresh the stored user so role and permission changes made by an admin show up after reload.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    fetch(getApiUrl('/users/me'), { headers: getAuthHeaders(token) })
      .then(response => (response.ok ? response.json() : null))
      .then((user: SessionUser | null) => {
        if (!user || cancelled || typeof user.role !== 'string') return;
        saveSessionUser(user);
        setSessionUser(user);
        setUserRole(user.role);
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    localStorage.setItem(VIEW_STORAGE_KEY, currentView);
  }, [currentView]);

  useEffect(() => {
    if (selectedPatientId === null) {
      localStorage.removeItem(PATIENT_ID_STORAGE_KEY);
      return;
    }
    localStorage.setItem(PATIENT_ID_STORAGE_KEY, String(selectedPatientId));
  }, [selectedPatientId]);

  useEffect(() => {
    if (selectedDoctorId === null) {
      localStorage.removeItem(DOCTOR_ID_STORAGE_KEY);
      return;
    }
    localStorage.setItem(DOCTOR_ID_STORAGE_KEY, String(selectedDoctorId));
  }, [selectedDoctorId]);

  useEffect(() => {
    if (!selectedDoctorName) {
      localStorage.removeItem(DOCTOR_NAME_STORAGE_KEY);
      return;
    }
    localStorage.setItem(DOCTOR_NAME_STORAGE_KEY, selectedDoctorName);
  }, [selectedDoctorName]);

  useEffect(() => {
    if (!selectedMonth) {
      localStorage.removeItem(MONTH_STORAGE_KEY);
      return;
    }
    localStorage.setItem(MONTH_STORAGE_KEY, selectedMonth);
  }, [selectedMonth]);

  useEffect(() => {
    if (selectedTreatmentId === null) {
      localStorage.removeItem(TREATMENT_ID_STORAGE_KEY);
      return;
    }
    localStorage.setItem(TREATMENT_ID_STORAGE_KEY, String(selectedTreatmentId));
  }, [selectedTreatmentId]);

  useEffect(() => {
    if (selectedTreatmentPatientId === null) {
      localStorage.removeItem(TREATMENT_PATIENT_ID_STORAGE_KEY);
      return;
    }
    localStorage.setItem(TREATMENT_PATIENT_ID_STORAGE_KEY, String(selectedTreatmentPatientId));
  }, [selectedTreatmentPatientId]);

  useEffect(() => {
    if (selectedTreatmentAppointmentId === null) {
      localStorage.removeItem(TREATMENT_APPOINTMENT_ID_STORAGE_KEY);
      return;
    }
    localStorage.setItem(TREATMENT_APPOINTMENT_ID_STORAGE_KEY, String(selectedTreatmentAppointmentId));
  }, [selectedTreatmentAppointmentId]);

  useEffect(() => {
    if ((currentView === 'users' || currentView === 'sheetImports' || currentView === 'treatments') && !isAdmin) {
      setCurrentView('home');
    }
    if ((currentView === 'doctors' || currentView === 'doctorProfile') && isDoctor) {
      setCurrentView('home');
    }
  }, [currentView, isAdmin, isDoctor]);

  const handleLogin = async (newToken: string, user?: SessionUser) => {
    saveSession(newToken, user);
    if (user) {
      setSessionUser(user);
      setUserRole(user.role);
    }
    setToken(newToken);
  };
  const handleLogout = () => {
    if (token) {
      // Best effort: revoke the server-side session; local state is cleared regardless.
      fetch(getApiUrl('/auth/logout'), { method: 'POST', headers: getAuthHeaders(token) }).catch(() => undefined);
    }
    clearSession();
    localStorage.removeItem(VIEW_STORAGE_KEY);
    localStorage.removeItem(PATIENT_ID_STORAGE_KEY);
    localStorage.removeItem(DOCTOR_ID_STORAGE_KEY);
    localStorage.removeItem(DOCTOR_NAME_STORAGE_KEY);
    localStorage.removeItem(MONTH_STORAGE_KEY);
    localStorage.removeItem(TREATMENT_ID_STORAGE_KEY);
    localStorage.removeItem(TREATMENT_PATIENT_ID_STORAGE_KEY);
    localStorage.removeItem(TREATMENT_APPOINTMENT_ID_STORAGE_KEY);
    setToken(null);
    setSessionUser(null);
    setUserRole(null);
    setSelectedPatientId(null);
    setSelectedDoctorId(null);
    setSelectedDoctorName('');
    setSelectedMonth('');
    setSelectedTreatmentId(null);
    setSelectedTreatmentPatientId(null);
    setSelectedTreatmentAppointmentId(null);
    setCurrentView('home');
  };

  const switchView = (view: ViewType) => {
    setMenuOpen(false);
    if ((view === 'doctors' || view === 'doctorProfile') && isDoctor) {
      setCurrentView('home');
      return;
    }
    setSelectedPatientId(null);
    setSelectedDoctorId(null);
    if (view !== 'treatmentWorkspace') {
      setSelectedTreatmentId(null);
      setSelectedTreatmentPatientId(null);
      setSelectedTreatmentAppointmentId(null);
    }
    setCurrentView(view);
  };
  

  const openCreatePatient = () => {
    setIntakeContext(null);
    setPendingCalendarConversion(null);
    switchView('createPatient');
  };

  const handleConvertIntakeRequest = (context: IntakeConversionContext) => {
    setPendingCalendarConversion(null);
    setIntakeContext(context);
    setSelectedPatientId(null);
    setCurrentView('createPatient');
  };

  const handleCancelIntakeConversion = () => {
    setIntakeContext(null);
    setPendingCalendarConversion(null);
    setCurrentView('calendar');
  };

  const handlePatientCreated = (patient: { id: number; name: string; email?: string | null; nextContact?: string | null; hasCriticalAllergy?: boolean; noShowCount?: number }, context?: IntakeConversionContext | null) => {
    if (context) {
      const calendarPatient: CalendarPatient = {
        id: patient.id,
        name: patient.name,
        email: patient.email ?? '',
        nextContact: patient.nextContact ?? null,
        hasCriticalAllergy: patient.hasCriticalAllergy ?? false,
        noShowCount: patient.noShowCount ?? 0
      };
      setPendingCalendarConversion({ intake: context, patient: calendarPatient });
      setIntakeContext(null);
      setCurrentView('calendar');
      return;
    }
    // For standalone patient creation keep existing behaviour (stay on form)
  };

  const layoutStyle = React.useMemo(() => ({
    ...baseLayoutStyle,

    background: tokens.app.background
  }), [sidebarCollapsed, tokens.app.background]);

  const sideNavStyle = React.useMemo<React.CSSProperties>(() => ({
    ...baseSideNavStyle,
    background: tokens.nav.background,
    color: tokens.nav.text,
    borderRight: `1px solid ${tokens.nav.border}`,

    padding: sidebarCollapsed ? '16px 8px' : '16px',
    alignItems: sidebarCollapsed ? 'center' : 'flex-start',
    boxSizing: 'border-box',
    overflow: 'hidden'
  }), [sidebarCollapsed, tokens.nav.background, tokens.nav.border, tokens.nav.text]);

  const brandStyle = React.useMemo(() => ({
    fontWeight: 800,
    letterSpacing: 0.5,
    marginBottom: sidebarCollapsed ? 8 : 16,
    fontSize: sidebarCollapsed ? 18 : 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
    width: '100%',
    gap: 8
  }), [sidebarCollapsed]);

  const toggleButtonStyle = React.useMemo<React.CSSProperties>(() => ({
    alignSelf: sidebarCollapsed ? 'center' : 'flex-end',
    background: tokens.nav.control,
    border: `1px solid ${tokens.nav.border}`,
    color: tokens.nav.text,
    padding: '6px',
    borderRadius: 6,
    cursor: 'pointer',
    width: sidebarCollapsed ? 32 : 36,
    height: sidebarCollapsed ? 32 : 36,
    display: 'grid',
    placeItems: 'center'
  }), [sidebarCollapsed, tokens.nav.border, tokens.nav.control, tokens.nav.text]);

  const navItemStyles = React.useMemo(() => {
    const base: React.CSSProperties = {
      padding: '10px 12px',
      borderRadius: 6,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: sidebarCollapsed ? 0 : 8,
      justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
      width: '100%',
      boxSizing: 'border-box',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    };
    return {
      active: {
        ...base,
        background: tokens.nav.activeBg,
        color: tokens.nav.activeText,
        fontWeight: 700
      } as React.CSSProperties,
      inactive: {
        ...base,
        color: tokens.nav.muted
      } as React.CSSProperties
    };
  }, [sidebarCollapsed, tokens.nav.activeBg, tokens.nav.activeText, tokens.nav.muted]);

  const themePickerStyle = React.useMemo<React.CSSProperties>(() => ({
    background: tokens.nav.panel,
    border: `1px solid ${tokens.nav.border}`,
    borderRadius: 10,
    padding: sidebarCollapsed ? 6 : 8,
    width: '100%',
    boxSizing: 'border-box',
    display: 'grid',
    gap: 6
  }), [sidebarCollapsed, tokens.nav.border, tokens.nav.panel]);

  const themeButtonStyle = React.useMemo<React.CSSProperties>(() => ({
    ...navItemStyles.inactive,
    background: themePickerOpen ? tokens.nav.activeBg : undefined,
    border: 0,
    font: 'inherit',
    textAlign: 'left'
  }), [navItemStyles.inactive, themePickerOpen, tokens.nav.activeBg]);

  const mainStyle = React.useMemo<React.CSSProperties>(() => ({
    ...contentStyle,

    background: tokens.app.background,
    color: tokens.app.text
  }), [currentView, tokens.app.background, tokens.app.text]);

  if (!token) {
    return <Login onLogin={handleLogin} />;
  }

  const handleOpenPatientProfile = (id: number) => {
    setSelectedPatientId(id);
  };

  const handleOpenDoctorProfile = (id: number) => {
    if (isDoctor) {
      setCurrentView('home');
      return;
    }
    setSelectedDoctorId(id);
    setCurrentView('doctorProfile');
  };

  const handleOpenDoctorTreatments = (doctorId: number, doctorName: string, month: string) => {
    setSelectedDoctorId(doctorId);
    setSelectedDoctorName(doctorName);
    setSelectedMonth(month);
    setCurrentView('doctorTreatments');
  };

  const handleOpenTreatmentWorkspace = (treatmentId: number, patientId?: number | null, appointmentId?: number | null) => {
    setSelectedPatientId(null);
    setSelectedDoctorId(null);
    setSelectedTreatmentId(treatmentId);
    setSelectedTreatmentPatientId(typeof patientId === 'number' ? patientId : null);
    setSelectedTreatmentAppointmentId(typeof appointmentId === 'number' ? appointmentId : null);
    setCurrentView('treatmentWorkspace');
  };

  return (
    <div className={`app-theme-shell ${sidebarCollapsed ? 'nav-collapsed' : ''} ${currentView === 'calendar' && !selectedPatientId ? 'calendar-active' : ''}`} data-theme={currentTheme} style={layoutStyle}>
      <InstallPrompt />
      <header className="phone-header" style={{ background: tokens.nav.background, color: tokens.nav.text }}>
        <button type="button" aria-label={t('responsive.openMenu')} aria-expanded={menuOpen} aria-controls="main-navigation" onClick={() => setMenuOpen(true)}>{t('responsive.menu')}</button>
        <strong style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <ClinixLogo size={24} />
          Clinix
        </strong>
      </header>
      {isPhone && menuOpen && <div className="nav-backdrop" onClick={closeMenu} />}
      <aside id="main-navigation" ref={navRef} className={`app-navigation ${menuOpen ? 'is-open' : ''}`} style={sideNavStyle} role={isPhone ? 'dialog' : undefined} aria-modal={isPhone && menuOpen ? true : undefined} aria-label={t('responsive.menu')}>
        {isPhone && <button type="button" onClick={closeMenu}>{t('responsive.closeMenu')}</button>}
        <div style={brandStyle}>
          <ClinixLogo size={sidebarCollapsed ? 32 : 30} />
          {!sidebarCollapsed && <span>Clinix</span>}
        </div>
        <button
          type="button"
          className="desktop-nav-toggle"
          onClick={() => setSidebarCollapsed(c => !c)}
          style={toggleButtonStyle}
          aria-label={sidebarCollapsed ? t('menu.expandNav') : t('menu.collapseNav')}
        >
          {sidebarCollapsed ? '>' : '<'}
        </button>
        <button type="button"
          style={currentView === 'home' ? navItemStyles.active : navItemStyles.inactive}
          onClick={() => switchView('home')}
        >
          <span role="img" aria-hidden="true">🏠</span>
          {!sidebarCollapsed && <span>{t('menu.home')}</span>}
        </button>
        <button type="button"
          data-testid="nav-calendar"
          style={currentView === 'calendar' ? navItemStyles.active : navItemStyles.inactive}
          onClick={() => switchView('calendar')}
        >
          <span role="img" aria-hidden="true">📅</span>
          {!sidebarCollapsed && <span>{t('menu.calendar')}</span>}
        </button>

        <button type="button"
          style={currentView === 'patients' ? navItemStyles.active : navItemStyles.inactive}
          onClick={() => switchView('patients')}
        >
          <span role="img" aria-hidden="true">👥</span>
          {!sidebarCollapsed && <span>{t('menu.patients')}</span>}
        </button>
        <button type="button"
          style={currentView === 'createPatient' ? navItemStyles.active : navItemStyles.inactive}
          onClick={openCreatePatient}
        >
          <span role="img" aria-hidden="true">➕</span>
          {!sidebarCollapsed && <span>{t('menu.createPatient')}</span>}
        </button>
        {!isDoctor && (
          <button type="button"
            style={currentView === 'doctors' ? navItemStyles.active : navItemStyles.inactive}
            onClick={() => switchView('doctors')}
          >
          <span role="img" aria-hidden="true">🩺</span>
          {!sidebarCollapsed && <span>{t('menu.doctors')}</span>}
          </button>
        )}
        <button type="button"
          style={currentView === 'allergies' ? navItemStyles.active : navItemStyles.inactive}
          onClick={() => switchView('allergies')}
        >
          <span role="img" aria-hidden="true">⚠️</span>
          {!sidebarCollapsed && <span>{t('menu.allergies')}</span>}
        </button>
        {isAdmin && (<button type="button"
          style={currentView === 'treatments' ? navItemStyles.active : navItemStyles.inactive}
          onClick={() => switchView('treatments')}
        >
          <span role="img" aria-hidden="true">🦷</span>
          {!sidebarCollapsed && <span>{t('menu.treatments')}</span>}
        </button>)}
        <button type="button"
          style={currentView === 'reports' ? navItemStyles.active : navItemStyles.inactive}
          onClick={() => switchView('reports')}
        >
          <span role="img" aria-hidden="true">📊</span>
          {!sidebarCollapsed && <span>{t('menu.reports')}</span>}
        </button>
        {canViewWhatsApp && <button type="button"
          style={currentView === 'whatsapp' ? navItemStyles.active : navItemStyles.inactive}
          onClick={() => switchView('whatsapp')}
          data-testid="nav-whatsapp"
        >
          <span role="img" aria-hidden="true">💬</span>
          {!sidebarCollapsed && <span>{t('menu.whatsapp', { defaultValue: 'WhatsApp' })}</span>}
        </button>}
        {isAdmin && (
          <button type="button"
            style={currentView === 'sheetImports' ? navItemStyles.active : navItemStyles.inactive}
            onClick={() => switchView('sheetImports')}
          >
            <span aria-hidden="true">GS</span>
            {!sidebarCollapsed && <span>{t('menu.sheetImports')}</span>}
          </button>
        )}
        {isAdmin && (
          <button type="button"
            style={currentView === 'users' ? navItemStyles.active : navItemStyles.inactive}
            onClick={() => switchView('users')}
          >
            <span role="img" aria-hidden="true">👥</span>
            {!sidebarCollapsed && <span>{t('menu.users')}</span>}
          </button>
        )}
        <div style={{ flex: 1 }} />
        <div style={themePickerStyle}>
          <button
            type="button"
            style={themeButtonStyle}
            onClick={() => setThemePickerOpen(open => !open)}
            aria-expanded={themePickerOpen}
            aria-label="Theme picker"
          >
            <span aria-hidden="true" style={{ display: 'inline-flex', width: 18, height: 18, borderRadius: 999, overflow: 'hidden', border: '1px solid rgba(226, 232, 240, 0.5)' }}>
              {activeTheme.colors.map(color => (
                <span key={color} style={{ background: color, flex: 1 }} />
              ))}
            </span>
            {!sidebarCollapsed && <span>{activeTheme.label}</span>}
          </button>
          {themePickerOpen && (
            <div style={{ display: 'grid', gap: 6 }}>
              {themeOptions.map(theme => (
                <button
                  key={theme.name}
                  type="button"
                  onClick={() => {
                    setCurrentTheme(theme.name);
                    setThemePickerOpen(false);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    gap: sidebarCollapsed ? 0 : 8,
                    width: '100%',
                    padding: sidebarCollapsed ? '8px 4px' : '8px 10px',
                    borderRadius: 8,
                    border: theme.name === currentTheme
                      ? `1px solid ${tokens.app.primary}`
                      : `1px solid ${tokens.nav.border}`,
                    background: theme.name === currentTheme
                      ? tokens.nav.activeBg
                      : tokens.nav.panel,
                    color: tokens.nav.text,
                    cursor: 'pointer',
                    font: 'inherit'
                  }}
                >
                  <span aria-hidden="true" style={{ display: 'inline-flex', width: 26, height: 16, borderRadius: 999, overflow: 'hidden', border: '1px solid rgba(226, 232, 240, 0.45)' }}>
                    {theme.colors.map(color => (
                      <span key={color} style={{ background: color, flex: 1 }} />
                    ))}
                  </span>
                  {!sidebarCollapsed && <span>{theme.label}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
        <button type="button"
          style={navItemStyles.inactive}
          onClick={() => {
            const newLang = i18n.language === 'en' ? 'es' : 'en';
            i18n.changeLanguage(newLang);
          }}
        >
          <span role="img" aria-hidden="true">🌐</span>
          {!sidebarCollapsed && <span>{i18n.language === 'en' ? 'Español' : 'English'}</span>}
        </button>
        <button type="button"
          style={navItemStyles.inactive}
          onClick={handleLogout}
        >
          <span role="img" aria-hidden="true">🚪</span>
          {!sidebarCollapsed && <span>{t('menu.logout')}</span>}
        </button>
        <button style={navItemStyles.inactive} onClick={() => { setSelectedPatientId(null); switchView('account'); }} title={t('staff.calendarTitle')}>{t('staff.calendarTitle')}</button>
      </aside>
      <main ref={mainRef} className={`app-content ${currentView === 'calendar' && !selectedPatientId ? 'calendar-content' : ''}`} style={mainStyle}>
        {selectedPatientId ? (
          <PatientProfile id={selectedPatientId} token={token} onBack={() => setSelectedPatientId(null)} />
        ) : (
          <>
            {currentView === 'calendar' && (
              <Calendar
                token={token}
                initialNavigationState={calendarNavigationState}
                onNavigationStateChange={setCalendarNavigationState}
                onOpenTreatmentWorkspace={handleOpenTreatmentWorkspace}
                onOpenPatientProfile={handleOpenPatientProfile}
                onConvertIntake={handleConvertIntakeRequest}
                pendingIntakeConversion={pendingCalendarConversion}
                onClearPendingIntake={() => setPendingCalendarConversion(null)}
                onOpenMenu={() => setMenuOpen(true)}
              />
            )}

            {currentView === 'home' && (
              <HomeDashboard
                token={token}
                onOpenPatient={handleOpenPatientProfile}
                onShowCalendar={() => switchView('calendar')}
              />
            )}
            {currentView === 'patients' && (
              <PatientList
                token={token}
                onSelect={setSelectedPatientId}
                onCreateNew={openCreatePatient}
                initialViewState={patientListViewState}
                onViewStateChange={setPatientListViewState}
              />
            )}
            {currentView === 'createPatient' && (
              <CreatePatient
                token={token}
                onCancel={intakeContext ? handleCancelIntakeConversion : () => switchView('patients')}
                onOpenProfile={handleOpenPatientProfile}
                onPatientCreated={handlePatientCreated}
                intakeContext={intakeContext}
              />
            )}
            {currentView === 'doctors' && !isDoctor && <DoctorsList token={token} onSelectDoctor={handleOpenDoctorProfile} />}
            {currentView === 'doctorProfile' && selectedDoctorId && !isDoctor && (
              <DoctorProfile 
                id={selectedDoctorId} 
                token={token} 
                onBack={() => switchView('doctors')}
                onSelectPatient={handleOpenPatientProfile}
              />
            )}
            {currentView === 'allergies' && <AllergiesCatalog token={token} />}
            {currentView === 'treatments' && isAdmin && <TreatmentsManagementPage token={token} />}
            {currentView === 'reports' && <ReportsPage onOpenDoctorTreatments={handleOpenDoctorTreatments} />}
            {currentView === 'whatsapp' && <WhatsAppPage token={token} />}
            {currentView === 'users' && isAdmin && <UserManagement token={token} />}
            {currentView === 'sheetImports' && isAdmin && (
              <GoogleSheetImportsPage
                token={token}
                onOpenPatient={handleOpenPatientProfile}
                onOpenTreatment={handleOpenTreatmentWorkspace}
              />
            )}
            {currentView === 'doctorTreatments' && selectedDoctorId && (
              <DoctorTreatmentsPage
                doctorId={selectedDoctorId}
                doctorName={selectedDoctorName}
                month={selectedMonth}
                onBack={() => setCurrentView('reports')}
              />
            )}
            {currentView === 'treatmentWorkspace' && selectedTreatmentId && (
              <TreatmentWorkspacePage
                token={token}
                treatmentId={selectedTreatmentId}
                patientId={selectedTreatmentPatientId}
                appointmentId={selectedTreatmentAppointmentId}
                onBack={() => setCurrentView('calendar')}
                onOpenPatientProfile={handleOpenPatientProfile}
              />
            )}
          </>
        )}
      </main>
    </div>
  );
}
